import React, {useEffect, useMemo} from 'react'
import {KonvaNodeEvents, Layer, Rect} from 'react-konva'
import {Node as NodeRef, NodeConfig, KonvaEventObject} from 'konva/lib/Node'
import {Rect as KonvaRect} from 'konva/lib/shapes/Rect'
import useImage from 'use-image'

export type GridLayerProps = {
  gridStepInPx: number
}

function svgToURL(s) {
  const uri = window.btoa(unescape(encodeURIComponent(s)))
  return 'data:image/svg+xml;base64,' + uri
}

export function GridLayer(props: GridLayerProps) {
  const layerRef = React.useRef<NodeRef>(null)
  const gridRectRef = React.useRef<KonvaRect>(null)

  const gridImageUrl = useMemo(() => {
    const x = props.gridStepInPx
    const svg = `
      <svg width="${x}" height="${x}"  xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="0" x2="0" y2="${x}" stroke="black" stroke-width="10"/>
        <line x1="0" y1="0" x2="${x}" y2="0" stroke="black" stroke-width="10"/>
      </svg>
    `
    return svgToURL(svg)
  }, [props.gridStepInPx])

  const [gridImage] = useImage(gridImageUrl)

  useEffect(() => {
    const stage = layerRef.current.getStage()

    const updateBackground = () => {
      const background = gridRectRef.current
      const {width, height} = stage.getSize()

      background.absolutePosition({x: 0, y: 0})
      background.size({
        width: width / stage.scaleX(),
        height: height / stage.scaleY(),
      })

      const stageOffset = {
        x: -stage.x() / stage.scaleX(),
        y: -stage.y() / stage.scaleY(),
      }

      background.fillPatternOffset(stageOffset)
    }

    const stageEventHandler: KonvaNodeEvents['onTransform'] = (e) => {
      if (e.currentTarget !== stage) {
        return
      }

      updateBackground()
    }

    updateBackground()

    stage.on('scaleXChange', stageEventHandler)
    // theoretically scale is uniform, so if X changes, y changes as well
    // stage.on('scaleYChange', stageEventHandler)
    stage.on('dragmove', stageEventHandler)

    return () => {
      if (stage) {
        stage.off('scaleXChange', stageEventHandler)
        // stage.off('scaleYChange', stageEventHandler)
        stage.off('dragmove', stageEventHandler)
      }
    }
  }, [gridImage])

  return (
    <Layer ref={layerRef as any}>
      <Rect
        ref={gridRectRef as any}
        strokeScaleEnabled={false}
        fillPatternImage={gridImage}
        listening={false}
      />
    </Layer>
  )
}
